import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { type IRateIndex, RateIndexValuesModal, useCanEditRateIndexValues } from 'components/RateIndexes'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { deleteRateIndex, getRateIndexes, syncRateIndexes, updateRateIndex } from 'services'
import { Toggle } from 'stories/components'
import { confirm, formatDate } from 'utils'

import { RateIndexModal } from './RateIndexModal'

/**
 * The indexes an adjustable-rate loan can follow, picked from the Index field on Servicing
 * Information.
 *
 * Standard indexes (WSJ Prime, SOFR, Treasury CMT...) are maintained by the platform: every tenant
 * has them, and they can be retired from the picker but not renamed or deleted. Anything else a
 * lender's notes follow is added here as a custom index. Each index keeps a dated history of its
 * values, so an adjustment can read the value as of its own date. Standard indexes with a public
 * source are refreshed every morning from the Federal Reserve; the rest are entered by hand.
 */
export const RateIndexesConfiguration = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<IRateIndex[]>([])
  // null = closed, 'new' = adding, otherwise the custom index being edited.
  const [editing, setEditing] = useState<IRateIndex | 'new' | null>(null)
  const [valuesFor, setValuesFor] = useState<IRateIndex | null>(null)
  const canEditValues = useCanEditRateIndexValues()

  useEffect(() => {
    initData()
  }, [])

  const initData = async () => {
    setLoading(true)
    try {
      setData((await getRateIndexes()) || [])
    } catch (error: any) {
      toast(error?.message || 'Could not load rate indexes', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onToggleActive = async (item: IRateIndex, isActive: boolean) => {
    setLoading(true)
    try {
      await updateRateIndex(item.key, { isActive })
      await initData()
    } catch (error: any) {
      toast(error?.message || 'Could not update the index', { type: 'error' })
      setLoading(false)
    }
  }

  const onSync = async () => {
    setLoading(true)
    try {
      const results: Array<{ key: string; added: number; error?: string }> = (await syncRateIndexes()) || []
      const failed = results.filter((r) => r.error)
      const added = results.reduce((sum, r) => sum + (r.added || 0), 0)
      if (failed.length) toast(`Could not update ${failed.map((r) => r.key).join(', ')}`, { type: 'error' })
      else toast(added ? `Recorded ${added} new value${added === 1 ? '' : 's'}` : 'Indexes are already up to date')
      await initData()
    } catch (error: any) {
      toast(error?.message || 'Could not update the indexes', { type: 'error' })
      setLoading(false)
    }
  }

  const onDelete = async (item: IRateIndex) => {
    const isContinue = await confirm(`Delete the "${item.name}" index and all of its values?`)
    if (!isContinue) return
    setLoading(true)
    try {
      await deleteRateIndex(item.key)
      await initData()
    } catch (error: any) {
      toast(error?.message || 'Could not delete the index', { type: 'error' })
      setLoading(false)
    }
  }

  const renderRow = (item: IRateIndex) => (
    <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700" key={item.key}>
      <td className="px-3 py-2">
        <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
          {item.name}
          {item.isStandard && (
            <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-shade-blue/10 text-shade-blue">
              Standard
            </span>
          )}
        </div>
        {item.description && <div className="text-xs text-gray-500 max-w-xl">{item.description}</div>}
      </td>
      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{item.source}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {item.latestValue === null ? (
          <span className="text-gray-400">No value yet</span>
        ) : (
          <>
            <span className="font-medium text-gray-900 dark:text-white">{item.latestValue}%</span>
            <span className="text-xs text-gray-500 ml-1">since {formatDate(item.latestEffectiveDate)}</span>
          </>
        )}
        <div className="text-xs">
          {!item.autoUpdated ? (
            <span className="text-gray-400">Entered by hand</span>
          ) : item.lastSyncError ? (
            <span className="text-red-700" title={item.lastSyncError}>
              Feed failed {item.lastSyncedAt ? formatDate(item.lastSyncedAt) : ''}
            </span>
          ) : (
            <span className="text-gray-400">
              Updated daily{item.lastSyncedAt ? `, checked ${formatDate(item.lastSyncedAt)}` : ''}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-gray-600">{item.loanCount || ''}</td>
      <td className="px-3 py-2">
        <Toggle
          id={`rate-index-active-${item.key}`}
          value={item.isActive}
          onChange={(value: boolean) => onToggleActive(item, value)}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="text-shade-blue hover:underline cursor-pointer whitespace-nowrap"
            onClick={() => setValuesFor(item)}
          >
            Values
          </span>
          {!item.isStandard && (
            <>
              <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => setEditing(item)}>
                <PencilSquareIcon className="w-4 h-4" />
              </span>
              <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(item)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </>
          )}
        </div>
      </td>
    </tr>
  )

  return (
    <div>
      <div className="flex items-center justify-between pb-2">
        <h1 className="text-2xl font-bold">Rate Indexes</h1>
        {canEditValues && (
          <button
            className="text-sm font-medium text-shade-blue hover:underline disabled:opacity-50"
            onClick={onSync}
            disabled={isLoading}
          >
            Update from Federal Reserve now
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 pb-5">
        Indexes an adjustable-rate loan can follow, chosen in the Index field on Servicing Information. Standard indexes
        are maintained by the platform, and most are refreshed every morning from the Federal Reserve; add your own for
        anything else your notes reference. Turning an index off hides it from the picker, and loans already on it keep
        it.
      </p>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                Index
              </th>
              <th scope="col" className="px-3 py-3">
                Source
              </th>
              <th scope="col" className="px-3 py-3">
                Latest Value
              </th>
              <th scope="col" className="px-3 py-3">
                Loans
              </th>
              <th scope="col" className="px-3 py-3">
                Active
              </th>
              <th scope="col" className="px-3 py-3">
                <button
                  className="font-variation-settings-600 text-sm font-medium text-shade-blue hover:underline"
                  onClick={() => setEditing('new')}
                >
                  + Add
                </button>
              </th>
            </tr>
          </thead>
          <tbody>{data.map(renderRow)}</tbody>
        </table>
      </div>

      {editing && (
        <RateIndexModal
          index={editing === 'new' ? null : editing}
          onClose={(changed) => {
            setEditing(null)
            if (changed) initData()
          }}
        />
      )}
      {valuesFor && (
        <RateIndexValuesModal
          index={valuesFor}
          onClose={(changed) => {
            setValuesFor(null)
            if (changed) initData()
          }}
        />
      )}
    </div>
  )
}
